/**
 * Health Categories Utility for Patient Dashboard
 * Maps quantitative test names to health categories for biomarker grouping.
 *
 * The category buckets and biomarker membership now come from the shared
 * biomarker registry (`@mld/shared/constants/biomarkers`). This module
 * just adapts the registry to the legacy HEALTH_CATEGORIES shape so all
 * existing call sites keep working unchanged.
 *
 * To add or rename a biomarker, edit the shared registry — both the BE
 * OHD merge (warehouse.ts) and these category mappings update from one
 * place.
 */
import {BIOMARKERS, HEALTH_CATEGORY_LABELS, type HealthCategoryKey} from '@mld/shared/constants/biomarkers';

export interface HealthCategory {
    name: string;
    tests: string[];
}

function buildCategoriesFromRegistry(): Record<HealthCategoryKey, HealthCategory> {
    const empty = (k: HealthCategoryKey): HealthCategory => ({
        name: HEALTH_CATEGORY_LABELS[k],
        tests: [],
    });
    const out: Record<HealthCategoryKey, HealthCategory> = {
        heart: empty('heart'),
        metabolic: empty('metabolic'),
        'liver-kidney': empty('liver-kidney'),
        micronutrient: empty('micronutrient'),
        hormone: empty('hormone'),
        other: empty('other'),
    };
    for (const b of BIOMARKERS) {
        out[b.category].tests.push(b.canonical);
        if (b.aliases) out[b.category].tests.push(...b.aliases);
    }
    return out;
}

export const HEALTH_CATEGORIES: Record<string, HealthCategory> = buildCategoriesFromRegistry();

// Reverse mapping for quick lookup. NAME-KEYED, and therefore only a FALLBACK
// — see getTestCategory. A display name is a label, not an identifier: it
// varies by source (assay_catalog.name "LDL Cholesterol" vs registry canonical
// "LDL" vs OHD's raw alias "ldl"), and renaming an assay silently reassigns
// its category. LOINC is the identifier.
export const TEST_TO_CATEGORY: Record<string, string> = {};
Object.entries(HEALTH_CATEGORIES).forEach(([categoryKey, category]) => {
    category.tests.forEach((testName) => {
        TEST_TO_CATEGORY[testName] = categoryKey;
    });
});

/** LOINC → category. The authoritative lookup: a LOINC identifies a biomarker
 *  across every source, so it survives display-name changes. */
export const CATEGORY_BY_LOINC: Record<string, string> = {};
for (const b of BIOMARKERS) {
    if (b.loinc) CATEGORY_BY_LOINC[b.loinc] = b.category;
}

/** Distinct biomarkers per category. NOT `category.tests.length`, which counts
 *  canonical + every alias and so inflates as aliases are added. */
export const BIOMARKER_COUNT_BY_CATEGORY: Record<string, number> = {};
for (const b of BIOMARKERS) {
    BIOMARKER_COUNT_BY_CATEGORY[b.category] = (BIOMARKER_COUNT_BY_CATEGORY[b.category] ?? 0) + 1;
}

/** Anything carrying enough identity to be categorized. Both key spellings are
 *  accepted because API results use `test_name` and ProcessedBiomarker uses
 *  `testName`; taking either keeps call sites from having to reshape. */
export interface CategorizableTest {
    /**
     * Resolved server-side from `biomarker_names` — the active display rules,
     * where names and categories are edited — and sent on the result.
     * Authoritative when present: it is the category a human set on the rule,
     * and it does not depend on this bundle's copy of the registry being
     * current.
     */
    category?: string | null;
    loinc?: string | null;
    test_name?: string | null;
    testName?: string | null;
}

const testDisplayName = (test: CategorizableTest): string => test.test_name ?? test.testName ?? '';

/** Guards against a category the UI has no bucket for. `groupTestsByCategory`
 *  only pre-creates the known keys, so an unrecognized value would push onto
 *  `undefined` and throw. Anything unknown degrades to 'other' instead. */
const isKnownCategory = (value: string | null | undefined): value is string =>
    !!value && Object.prototype.hasOwnProperty.call(HEALTH_CATEGORIES, value);

/**
 * Get the category key for a test, in descending order of authority:
 *
 *   1. `category` resolved by the API from the display rules (biomarker_names)
 *   2. the LOINC, via this bundle's copy of the registry
 *   3. the display name (only useful for rows with no LOINC at all)
 *   4. 'other'
 *
 * Accepts a bare string for legacy callers that genuinely only have a name,
 * but prefer passing the whole result — a name-only call reaches neither of
 * the first two, and is exactly how markers used to fall into 'other' when an
 * assay was renamed.
 */
export const getTestCategory = (test: CategorizableTest | string): string => {
    if (typeof test === 'string') return TEST_TO_CATEGORY[test] || 'other';

    if (isKnownCategory(test.category)) return test.category;

    const loinc = test.loinc?.trim();
    if (loinc && CATEGORY_BY_LOINC[loinc]) return CATEGORY_BY_LOINC[loinc];

    return TEST_TO_CATEGORY[testDisplayName(test)] || 'other';
};

/**
 * Get the full category object for a test
 * If it doesn't resolve to a category, returns the 'other' category
 */
export const getTestCategoryInfo = (test: CategorizableTest | string): HealthCategory => {
    const categoryKey = getTestCategory(test);
    return HEALTH_CATEGORIES[categoryKey];
};

/**
 * Get all unmapped test names from a list of results
 * These are tests that resolve to no category by LOINC or by name
 */
export const getUnmappedTests = (results: Array<{test_name: string; [key: string]: any}>): string[] => {
    return results.filter((result) => getTestCategory(result) === 'other').map((result) => result.test_name);
};

/**
 * Filter test results by category
 */
export const filterTestsByCategory = (
    results: Array<{test_name: string; result: string; [key: string]: any}>,
    categoryKey: string
): Array<{test_name: string; result: string; [key: string]: any}> => {
    if (!HEALTH_CATEGORIES[categoryKey]) return [];

    return results.filter((result) => getTestCategory(result) === categoryKey);
};

/**
 * Group test results by health categories
 * Automatically places unmapped tests into the 'other' category
 */
export const groupTestsByCategory = (
    results: Array<{test_name: string; result: string; [key: string]: any}>
): Record<string, Array<{test_name: string; result: string; [key: string]: any}>> => {
    const grouped: Record<string, Array<{test_name: string; result: string; [key: string]: any}>> = {};

    // Initialize all categories with empty arrays
    Object.keys(HEALTH_CATEGORIES).forEach((categoryKey) => {
        grouped[categoryKey] = [];
    });

    // Group each test result into its appropriate category. Passes the whole
    // result, not just the name, so the LOINC path is used when available.
    results.forEach((result) => {
        const categoryKey = getTestCategory(result);
        grouped[categoryKey].push(result);
    });

    return grouped;
};

/**
 * Parse a numeric value from a result string
 */
const parseNumericValue = (value: string): number | null => {
    if (!value) return null;

    // Remove common non-numeric characters and extract number
    const cleaned = value.replace(/[<>≤≥]/g, '').trim();
    const match = cleaned.match(/[\d.]+/);

    if (match) {
        const parsed = parseFloat(match[0]);
        return isNaN(parsed) ? null : parsed;
    }

    return null;
};

/**
 * Parse reference range string (e.g., "30-100", "< 5.7", "2.5 - 4.5")
 */
const parseReferenceRange = (refRange: string): {min: number; max: number} | null => {
    if (!refRange) return null;

    // Handle different reference range formats
    const cleaned = refRange.replace(/[≤≥]/g, '').trim();

    // Format: "min - max" or "min-max"
    const rangeMatch = cleaned.match(/(\d+\.?\d*)\s*-\s*(\d+\.?\d*)/);
    if (rangeMatch) {
        const min = parseFloat(rangeMatch[1]);
        const max = parseFloat(rangeMatch[2]);
        if (!isNaN(min) && !isNaN(max)) {
            return {min, max};
        }
    }

    // Format: "< value" (upper limit only)
    const upperMatch = cleaned.match(/[<]\s*(\d+\.?\d*)/);
    if (upperMatch) {
        const max = parseFloat(upperMatch[1]);
        if (!isNaN(max)) {
            return {min: 0, max};
        }
    }

    // Format: "> value" (lower limit only)
    const lowerMatch = cleaned.match(/[>]\s*(\d+\.?\d*)/);
    if (lowerMatch) {
        const min = parseFloat(lowerMatch[1]);
        if (!isNaN(min)) {
            return {min, max: min * 2}; // Approximate upper bound
        }
    }

    return null;
};

/**
 * Normalize a test value based on its reference range (0-100 scale)
 * 50 = middle of normal range, <50 = below normal, >50 = above normal
 */
const normalizeTestValue = (result: {result: string; refRange?: string}): number | null => {
    const numericValue = parseNumericValue(result.result);
    if (numericValue === null) return null;

    // If no reference range, return the raw value (fallback)
    if (!result.refRange) return numericValue;

    const range = parseReferenceRange(result.refRange);
    if (!range) return numericValue;

    // Calculate position within the range (0-100%)
    const position = ((numericValue - range.min) / (range.max - range.min)) * 100;

    // Return normalized score (0-100, can go beyond for values outside range)
    return Math.max(0, position);
};

/**
 * Get summary statistics for each category
 */
export const getCategoryStats = (
    results: Array<{test_name: string; result: string; refRange?: string; [key: string]: any}>
) => {
    const grouped = groupTestsByCategory(results);

    return Object.entries(HEALTH_CATEGORIES).map(([categoryKey, category]) => {
        const categoryResults = grouped[categoryKey];

        // Calculate average normalized value for the category (based on reference ranges)
        const normalizedValues = categoryResults
            .map((result) => normalizeTestValue(result))
            .filter((value): value is number => value !== null);

        const averageValue =
            normalizedValues.length > 0
                ? normalizedValues.reduce((sum, val) => sum + val, 0) / normalizedValues.length
                : 0;

        return {
            key: categoryKey,
            name: category.name,
            // Distinct biomarkers, not name variants: category.tests holds
            // canonical + aliases, so counting it inflated the total every
            // time an alias was added.
            totalTests: BIOMARKER_COUNT_BY_CATEGORY[categoryKey] ?? 0,
            availableTests: categoryResults.length,
            averageValue: Math.round(averageValue), // Round to whole number for display
            tests: categoryResults,
        };
    });
};

export default HEALTH_CATEGORIES;
